import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { isThirdPartyVirtualWarehouse, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingDispatch, WarehousingItemSummary, WarehousingMasterData, WarehousingSearchFilters } from '../warehousing.model';
import { itemIdentityLabel } from '../warehousing-item-display';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-dispatch',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, AutoCompleteModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    TagModule, ToastModule, WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-dispatch.component.html'
})
export class WarehousingDispatchComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private permissionService = inject(PermissionService);
  readonly canCreateGoodsOut = this.permissionService.hasPermission(app_permissions.warehousing.dispatch.create)
    && this.permissionService.hasPermission(app_permissions.warehousing.dispatch.release);
  isLoading = false;
  isSubmitting = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Dispatches');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  dispatches: WarehousingDispatch[] = [];
  clients: CrmClient[] = [];
  warehouses: WarehousingMasterData[] = [];
  itemOptions: Array<WarehousingItemSummary & { displayLabel: string }> = [];
  private itemSearchRequest = 0;
  dispatchStatusOptions = [
    { label: 'Planned', value: 'PLANNED' },
    { label: 'Reserved', value: 'RESERVED' },
    { label: 'Allocated', value: 'ALLOCATED' },
    { label: 'Picked', value: 'PICKED' },
    { label: 'Staged', value: 'STAGED' },
    { label: 'Loaded', value: 'LOADED' },
    { label: 'Dispatched', value: 'DISPATCHED' },
    { label: 'Delivered', value: 'DELIVERED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];
  dispatchSourceOptions = [
    { label: 'Goods Out', value: 'MANUAL_GOODS_OUT' },
    { label: 'Manual', value: 'MANUAL' },
    { label: 'POSM Order Release', value: 'POSM_ORDER_RELEASE' },
    { label: 'Sales Order', value: 'SALES_ORDER' }
  ];

  searchForm = this.fb.group({
    keyword: [''],
    item: [null as (WarehousingItemSummary & { displayLabel: string }) | null],
    warehouseId: [null as number | null],
    crmClientId: [null as number | null],
    sourceType: [null as string | null],
    status: [null as string | null],
    scheduledFrom: [''],
    scheduledTo: ['']
  });

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.get('sourceType') === 'MANUAL_GOODS_OUT') {
      this.searchForm.patchValue({ sourceType: 'MANUAL_GOODS_OUT' });
    }
    this.loadDispatches();
  }

  loadDispatches(): void {
    this.isLoading = true;
    forkJoin({
      dispatches: this.warehousingService.getDispatches(this.searchFilters()),
      clients: this.crmService.getClients(),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE')
    }).subscribe({
      next: response => {
        this.dispatches = response.dispatches.data || [];
        this.clients = response.clients || [];
        this.warehouses = response.warehouses.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.loadDispatches();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      item: null,
      warehouseId: null,
      crmClientId: null,
      sourceType: null,
      status: null,
      scheduledFrom: '',
      scheduledTo: ''
    });
    this.itemOptions = [];
    this.itemSearchRequest++;
    this.loadDispatches();
  }

  searchItemOptions(event: { query: string }): void {
    const request = ++this.itemSearchRequest;
    const keyword = event.query.trim();
    if (!keyword) { this.itemOptions = []; return; }
    this.warehousingService.getItemSummaries({ keyword }, 0, 20).subscribe({
      next: response => {
        if (request === this.itemSearchRequest) {
          this.itemOptions = (response.data?.items || []).map(item => ({ ...item, displayLabel: itemIdentityLabel(item) }));
        }
      },
      error: () => { if (request === this.itemSearchRequest) this.itemOptions = []; }
    });
  }

  reserveDispatch(dispatch: WarehousingDispatch): void {
    if (!dispatch.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.reserveDispatch(dispatch.id).pipe(finalize(() => this.isSubmitting = false)).subscribe({
      next: resp => {
        if (!resp.success || !resp.data) return;
        const inherited = (resp.data.lines || []).some(line => (line.reservations || []).some(reservation => reservation.sourceReservationType || reservation.sourceReservationId));
        this.messageService.add({
          severity: 'success',
          summary: 'Reserved',
          detail: inherited ? 'Dispatch bound reserved units from upstream warehouse reservations.' : 'Stock has been reserved.'
        });
        this.loadDispatches();
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Reserve Failed',
        detail: error?.error?.message || 'Unable to reserve dispatch stock.'
      })
    });
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  lifecycleStatus(dispatch: WarehousingDispatch): string {
    return dispatch.lifecycleStatus || dispatch.status || '-';
  }

  showLifecycleTag(dispatch: WarehousingDispatch): boolean {
    const visibleStatus = (dispatch.status || '').toUpperCase();
    const lifecycleStatus = (dispatch.lifecycleStatus || dispatch.status || '').toUpperCase();
    return !!dispatch.lifecycleStatus && lifecycleStatus !== visibleStatus;
  }

  sourceLabel(dispatch: WarehousingDispatch): string {
    return dispatch.sourceNumber || dispatch.referenceNumber || 'Manual dispatch';
  }

  transportSummary(dispatch: WarehousingDispatch): string {
    if (dispatch.shipmentNumber) {
      return dispatch.shipmentNumber;
    }
    if (dispatch.transportReleaseNumber) {
      return dispatch.transportReleaseNumber;
    }
    return 'No transport linked';
  }

  quantitiesSummary(dispatch: WarehousingDispatch): string {
    const lines = dispatch.lines || [];
    const requested = lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0);
    const reserved = lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0);
    const exited = lines.reduce((sum, line) => sum + (line.quantityExited || 0), 0);
    return `Req ${requested} | Res ${reserved} | Exit ${exited}`;
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return this.warehouses.find(row => row.id === id);
  }

  usesVirtualWarehouse(dispatch: WarehousingDispatch): boolean {
    return (dispatch.lines || []).some(line => isThirdPartyVirtualWarehouse(this.warehouseById(line.warehouseId)));
  }

  isGateOpen(dispatch: WarehousingDispatch): boolean {
    return !dispatch.gatedByTransferId || dispatch.crossDockGateOpen === true;
  }

  canReserve(dispatch: WarehousingDispatch): boolean {
    return !this.usesVirtualWarehouse(dispatch)
      && this.isGateOpen(dispatch)
      && ['PLANNED', 'DRAFT'].includes(this.lifecycleStatus(dispatch).toUpperCase());
  }

  canContinue(dispatch: WarehousingDispatch): boolean {
    return !this.usesVirtualWarehouse(dispatch)
      && ['RESERVED', 'ALLOCATED', 'PICKED', 'STAGED', 'LOADED'].includes(this.lifecycleStatus(dispatch).toUpperCase());
  }

  statusSeverity = statusSeverity;

  private searchFilters(): WarehousingSearchFilters {
    const { item, ...filters } = this.searchForm.getRawValue();
    return { ...filters, itemId: item?.id || null };
  }
}
